import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
    AppBar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Container,
    Dialog,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Toolbar,
    Typography
} from "@mui/material";
import { Helmet } from "react-helmet";
import { ArrowBack, NavigateNext, Padding } from '@mui/icons-material';
import { useParams } from "react-router-dom";
import Hero2 from "../../components/DestinationHero";
import Filters from "../../components/Filters";
import ProductCard2 from "../../components/DestinationProductCard";
import { dataPage } from "../../utils/destinationPageMockData";
import { ReactComponent as Filter } from "../../assets/icons/filter.svg"
import MainLayout from "../../components/MainLayout";
import useFetchTourList from "../../hooks/API/Tours/useFetchTourList";
import useFetchCategories from "../../hooks/API/Tours/useFetchCategories";
import useFetchAttractions from "../../hooks/API/Tours/useFetchAttractions";
import axios from "axios";
import TagButton from '../../components/TagButton';
import { getHrefFromGuide, getHrefFromAttraction } from '../../utils/guias'



const DynamicContent = ({ content }) => {
    return (
        content.map(item => (
            <Typography
                key={item.value}
                sx={{ py: '10px' }}
                variant={item.variant}
                component={item.component}
            >{item.value}</Typography>
        ))
    )
}

const GroupLinks = ({ links }) => (
    links.map(link => (
        <Button
            key={link.text}
            sx={{
                fontSize: '14px',
                mx: '5px',
                my: '6px'
            }}
            variant="contained"
            href={link.href}
        >{link.text}</Button>
    ))
)

const FiltersSkeleton = () => (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Skeleton variant="text" width="50%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="45%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="35%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" height={36} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={36} sx={{ flex: 1 }} />
        </Box>
    </Box>
);

const ToursListSkeleton = ({ count = 6 }) => (
    <Box>
        {Array.from({ length: count }).map((_, i) => (
            <Box key={`tour-skeleton-${i}`} sx={{ mb: 2 }}>
                <Skeleton variant="rounded" height={180} />
            </Box>
        ))}
    </Box>
);

const TourList = () => {
    const { t } = useTranslation(['tours', 'paths', 'components']);
    const { i18n } = useTranslation();
    let params = useParams();
    const [filteredData, setFilteredData] = useState(null);
    const [orderBy, setOrderBy] = useState('precio');
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tours, setTours] = useState([]);
    const [destination, setDestination] = useState({});
    const [error, setError] = useState(null);
    const [hero, setHero] = useState({});
    const [totalReviews, setTotalReviews] = useState(0);
    const [totalTours, setTotalTours] = useState(0);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [content, setContent] = useState('');
    const [filters, setFilters] = useState({});
    const [priceRange, setPriceRange] = useState([]);
    const [guide, setGuide] = useState([]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    const {
        activities,
        description,
        numberActivities,
        atracciones,
        guias } = dataPage;
    const [page, setPage] = React.useState(1);

    const { attractions, isLoadingAtt } = useFetchAttractions(params.city);

    const handleChangePage = (event, value) => {
        setPage(value);
    };

    const fetchTourList = async (filters) => {
        setIsLoading(true);
        const { REACT_APP_BASE_URL } = process.env;
        const token = localStorage.getItem('token');

        try {
            const cityName = filters.city || params.city; // Usa la ciudad del filtro si está disponible
            console.log("Fetching tours for city:", cityName, "with filters:", filters);

            const { data } = await axios.post(
                `${REACT_APP_BASE_URL}/index.php?r=api/tours/tourlist`,
                filters,  // Se envía el JSON como payload en POST
                {
                    params: {
                        city: cityName,
                        lng: i18n.language
                    },
                    headers: {
                        'x-authorization-token': token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setIsLoading(false);

            if (data?.tours) {
                setPriceRange(data.priceRange);
                setTours(data.tours);
                setDestination(data.city);
                setGuide(data.city?.guide);
                let totalReviewsCount = 0;
                let rating = 0;
                data.tours.forEach(tour => {
                    const reviews = tour.reviewsTotal;
                    totalReviewsCount += reviews;

                    if (reviews >= 0 && reviews < 250) {
                        rating += 7;
                    } else if (reviews >= 250 && reviews <= 1000) {
                        rating += 9;
                    } else if (reviews > 1000) {
                        rating += 10;
                    }
                });

                const totalToursRating = (rating / data.tours.length).toFixed(1);

                setHero({
                    country: data.country.name,
                    city: data.city.name,
                    numberComments: totalReviewsCount,
                    media: `${totalToursRating}/10`,
                    numberActivities: data.count,
                    bgImage: data.city.image
                });

                setContent(data.city.content);
                setTotalTours(data.count);
                setBreadcrumbs([
                    { text: t('paths:home-title'), href: '/', isCurrent: false },
                    { text: t('paths:destinations-title'), href: `/${t('paths:destinations')}`, isCurrent: false },
                    { text: data.country.name, href: `/${t('paths:destinations')}/${data.country.slug}`, isCurrent: false },
                    { text: data.city.name, href: '#', isCurrent: true }
                ]);
            }
        } catch (err) {
            console.error('Error fetching tour details ', err);
            setError(true);
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchTourList(filters);
    }, [params.city]);

    return (
        <MainLayout>
            <Helmet>
                <title>{destination.name ? t('page', { city: destination.name }) : t('page-dafault')}</title>
                <meta
                    name="description"
                    content={destination.shortDescription}
                    data-react-helmet="true"
                />
            </Helmet>
            <Helmet>
                <meta
                    name="robots"
                    content="index, follow"
                    data-react-helmet="true"
                />
            </Helmet>
            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                color="secondary"
            >
                <AppBar sx={{
                    pr: "0!important",
                    position: 'relative',
                    backgroundColor: '#ffffff'
                }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="primary"
                            onClick={handleClose}
                            aria-label="close"

                        >
                            <ArrowBack />
                        </IconButton>
                    </Toolbar>
                    <Box>
                        {isLoading ? (
                            <FiltersSkeleton />
                        ) : (
                            <Filters city={params.city} maxPrice={tours?.maxPrice} onApplyFilters={(newFilters) => {
                                setFilters(newFilters); // Actualiza los filtros
                                fetchTourList(newFilters); // Llama a la API con los nuevos filtros
                            }} />
                        )}
                    </Box>
                </AppBar>
            </Dialog>
            <Hero2 {...hero} />
            <div className='pattern-img pattern-top' />
            <Container fixed
                sx={{
                    pt: '50px',
                    maxWidth: {
                        xs: '100%',      // pantallas muy pequeñas, ancho completo
                        sm: '95%',       // pantallas pequeñas
                        md: '1200px',    // personalizado en pantallas medianas
                        lg: '1200px',    // pantallas grandes
                    }
                }}
            >
                <Grid container spacing={4} columns={18}>
                    <Grid item
                        xs={12}
                        sm={5}
                        sx={{ display: { xs: 'none', sm: 'block' }, paddingLeft: '0px' }}
                    >
                        <Box>
                            {isLoading ? (
                                <FiltersSkeleton />
                            ) : (
                                <Filters city={params.city} priceRange={priceRange} maxPrice={priceRange?.max} onApplyFilters={(newFilters) => {
                                    setFilters(newFilters);
                                    fetchTourList(newFilters);
                                }} />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={13}>
                        {breadcrumbs && <Breadcrumbs
                            separator={<NavigateNext fontSize="small" />}
                            aria-label="breadcrumb"
                            sx={{ pb: '20px' }}
                        >
                            {breadcrumbs.map(item => (
                                <Link
                                    key={item.text}
                                    underline="hover"
                                    color={item.isCurrent ? "secondary" : "primary"}
                                    href={item.href}
                                    sx={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {item.text}
                                </Link>
                            ))}
                        </Breadcrumbs>}
                        <Typography
                            sx={{ fontSize: '14px' }}
                            variant="body2"
                            component="p"
                        >
                            {destination.shortDescription}
                        </Typography>
                        <Divider sx={{ pt: '15px' }} />
                        <Box sx={{ pt: '20px' }}>
                            <Box
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                    mb: '15px'
                                }}>
                                <Typography
                                    sx={{ fontSize: '14px' }}
                                    color="#666B6E"
                                    variant="body2"
                                    component="h2"
                                >
                                    {t('list-activities', { activities: totalTours, city: hero.city })}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    pb: "20px",
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}>
                                <Typography
                                    sx={{ fontSize: '14px', display: { xs: 'none', md: 'block' } }}
                                    color="#666B6E"
                                    variant="body2"
                                    component="h2"
                                >
                                    {t('list-activities', { activities: totalTours, city: hero.city })}
                                </Typography>
                                <Button
                                    onClick={handleClickOpen}
                                    className="d-none d-sm-block"
                                    sx={{
                                        display: { xs: 'block', md: 'none' },
                                        color: '#666B6E',
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '5px',
                                        fontWeight: '200'
                                    }}
                                >
                                    <Filter />&nbsp;{t('components:filters')}
                                </Button>
                                <FormControl>
                                    <InputLabel id="order-by-labe">{t('components:orderby')}</InputLabel>
                                    <Select
                                        sx={{ width: '120px', height: '40px' }}
                                        labelId="order-by-label"
                                        id="order-by-labe"
                                        value={orderBy}
                                        label="Ordenar por"
                                        onChange={() => { }}
                                    >
                                        <MenuItem value='precio'>{t('components:price')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            {isLoading ? (
                                <ToursListSkeleton count={6} />
                            ) : (
                                (tours.length > 0) && tours.map((activity, index) => (
                                    <Link
                                        key={index}
                                        underline="none"
                                        href={`/tour/${activity.city.slug}/${activity.slug}`}
                                    >
                                        <ProductCard2 key={index} {...activity} />
                                    </Link>
                                ))
                            )}
                            <Card id="card-city-summary" sx={{ mt: '30px', mb: '30px', 'box-shadow': '1px 6px 15px -1px rgba(0,0,0,0.3) !important;' }}>
                                <CardContent>
                                    {/* <DynamicContent content={content} /> */}
                                    <div dangerouslySetInnerHTML={{ __html: content }} />
                                </CardContent>
                            </Card>
                            <Box sx={{ mt: '30px' }}>
                                <Typography color='secondary' variant='subtitle' component="h2" align="center">
                                    {t('tours:attractions-list', { city: destination.name })}
                                </Typography>
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'flex',
                                            md: 'flex',
                                        },
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        gap: '1rem',
                                        py: '1rem',
                                        px: { xs: '.5rem', md: '1.5rem' },
                                    }}
                                >
                                    {attractions.map((attraction, index) => {
                                        return (
                                            <TagButton
                                                key={`${attraction.name}${index}`}
                                                text={attraction.name}
                                                href={getHrefFromAttraction(attraction, t('guides:path'), t('guides:pathattractions', { city: attraction.city_slug }))}
                                                sx={{ height: '2rem', padding: '1.2rem 1rem' }}
                                                title={`${t("guideTabs-search-tagbutton")} ${attraction.name}`}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                            {guide && Object.keys(guide).length > 0 && (
                                <Box sx={{ mt: '30px', mb: '50px' }}>
                                    <Typography color='secondary' variant='subtitle' component="h2" align="center">
                                        {t('tours:guide-title', { city: destination.name })}
                                    </Typography>
                                    <Typography color='secondary' variant='body1' component="p" align="center" sx={{ mb: '20px' }}>
                                        {guide?.description || t('tours:guide-description-default')}
                                    </Typography>
                                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                        <TagButton
                                            key={`${destination.name}-guide`}
                                            text={t('tours:guide-button-text', { city: destination.name })}
                                            href={getHrefFromGuide(guide, t('guides:path'))}
                                            sx={{ height: '2rem', padding: '1.2rem 1rem' }}
                                            title={t('tours:guide-button-title', { city: destination.name })}
                                        />
                                    </div>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </MainLayout>
    )
}

export default TourList;
